import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Typography,
  Card,
  Space,
  Input,
  Select,
  DatePicker,
  Button,
  Tag,
  Row,
  Col,
  Pagination,
  message,
  Tooltip,
  Badge,
  Spin,
} from 'antd';
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined, 
  HistoryOutlined,
  FilterOutlined,
  PictureOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { DataTable, PageLayout } from '../../ui';
import { 
  useGetAllCargoQuery,
  useUpdateCargoStatusMutation,
  useMoveCargoToWarehouseBulkMutation
} from '../../store/api/cargoApi';
import { useGetApplicationsQuery } from '../../store/api/applicationsApi';
import { useGetAllWarehousesQuery } from '../../store/api/warehousesApi';
import { 
  CargoFilters, 
  CargoStatus, 
  ApplicationCargo, 
  CARGO_STATUS_LABELS,
  CARGO_STATUS_COLORS,
  Route,
} from '../../types';
import CargoDetailsModal from '../../components/cargo/CargoDetailsModal';
import CargoDocumentsModal from '../../components/cargo/CargoDocumentsModal';
import CargoHistoryModal from '../../components/cargo/CargoHistoryModal';
import LocationHistoryModal from '../../components/common/LocationHistoryModal';
import dayjs from 'dayjs';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { buildRouteDirection } from '../../utils/addressUtils';
import { transportStatusTagColor } from '../../utils/cargoDerivedTransportStatus';
import { fetchCargoAllocationIndexForApplications } from '../../utils/fetchCargoAllocationIndexForApplications';
import { isCargoLifecycleProjectionUiEnabled } from '../../services/cargoLifecycle/cargoLifecycleProjectionFlags';
import { useCargoLifecycleProjectionIndex } from '../../hooks/useCargoLifecycleProjectionIndex';
import { resolveCargoCounterSnapshot } from '../../utils/cargoCounterSnapshot';
import type { CargoCounterSnapshot } from '../../services/cargoLifecycle/cargoLifecycleProjectionTypes';
import { routesApi } from '../../store/api/routesApi';
import type { AppDispatch } from '../../store';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

function formatDimSafe(n?: number, digits = 2): string {
  if (n === undefined || n === null) return '0';
  const num = Number(n);
  if (!Number.isFinite(num)) return '0';
  return Number(num.toFixed(digits)).toString();
}

const CargoPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const projectionUiEnabled = isCargoLifecycleProjectionUiEnabled();
  const [allocationIndex, setAllocationIndex] = useState(
    () => new Map<number, { inRoutesTotal: number; byRoute: { routeId: number; amount: number }[] }>()
  );
  const [allocationIndexLoading, setAllocationIndexLoading] = useState(false);
  const [routeStatusById, setRouteStatusById] = useState(() => new Map<number, string>());
  const [routeDetailById, setRouteDetailById] = useState(() => new Map<number, Route>());

  // Состояние фильтров
  const [filters, setFilters] = useState<CargoFilters>({
    page: 1,
    limit: 20
  });
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [locationHistoryModalVisible, setLocationHistoryModalVisible] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<ApplicationCargo | null>(null);

  // API запросы
  const { 
    data: cargoResponse, 
    isLoading, 
    refetch,
    fulfilledTimeStamp,
  } = useGetAllCargoQuery(filters);
  const { data: applicationsData } = useGetApplicationsQuery({ page: 1, limit: 100 });
  const applications = useMemo(() => applicationsData?.applications || [], [applicationsData]);

  const [updateCargoStatus] = useUpdateCargoStatusMutation();
  const [moveBulk, { isLoading: isMoving }] = useMoveCargoToWarehouseBulkMutation();
  const { data: warehouses } = useGetAllWarehousesQuery();

  const pageCargoRows = useMemo(
    () => cargoResponse?.data?.cargo ?? [],
    [cargoResponse?.data?.cargo]
  );

  const pageCargoIds = useMemo(
    () =>
      pageCargoRows
        .map((c) => Number(c.id))
        .filter((n) => Number.isFinite(n) && n > 0),
    [pageCargoRows]
  );

  const {
    index: projectionIndex,
    loading: projectionIndexLoading,
    failed: projectionIndexFailed,
  } = useCargoLifecycleProjectionIndex(pageCargoIds, {
    enabled: projectionUiEnabled,
    refreshKey: `${fulfilledTimeStamp ?? ''}|${pageCargoIds.join(',')}`,
  });

  const resolveCounters = useCallback(
    (record: ApplicationCargo): CargoCounterSnapshot =>
      resolveCargoCounterSnapshot({
        cargoId: record.id,
        quantity: Number(record.quantity ?? 0),
        projectionIndex,
        legacyIndex: allocationIndex,
        routeStatusById,
        useProjection: projectionUiEnabled,
      }),
    [projectionIndex, allocationIndex, routeStatusById, projectionUiEnabled]
  );

  const countersLoading = projectionUiEnabled ? projectionIndexLoading : allocationIndexLoading;

  const pageApplicationIdsKey = useMemo(() => {
    const ids = new Set<number>();
    for (const c of pageCargoRows) {
      const aid = c.applicationId ?? c.application?.id;
      if (aid != null && Number.isFinite(Number(aid))) ids.add(Number(aid));
    }
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(',');
  }, [pageCargoRows]);

  const pageRoutesFingerprint = useMemo(() => {
    const parts: string[] = [];
    for (const c of pageCargoRows) {
      const app = c.application as
        | { routes?: Array<{ route?: { id?: number }; routeId?: number }> }
        | undefined;
      const rids = (app?.routes || [])
        .map((ra) => Number(ra?.route?.id ?? ra?.routeId))
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
        .join('-');
      const aid = Number(c.applicationId ?? c.application?.id ?? 0);
      const allocHint = [c.allocatedQuantityTotal, c.availableQuantity]
        .map((x: unknown) => {
          if (x == null) return null;
          if (typeof x === 'string' && x.trim() === '') return null;
          const n = Number(x);
          return Number.isFinite(n) ? n : null;
        })
        .filter((n): n is number => n != null)
        .join(':');
      parts.push(`${aid}:${rids}:${allocHint}:${c.id}`);
    }
    return parts.sort().join('|');
  }, [pageCargoRows]);

  const allocationIndexFingerprint = useMemo(() => {
    if (!allocationIndex.size) return '';
    return Array.from(allocationIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(
        ([cid, v]) =>
          `${cid}:${v.inRoutesTotal}:${v.byRoute.map((x) => `${x.routeId}:${x.amount}`).sort().join(',')}`
      )
      .join('|');
  }, [allocationIndex]);

  const allocationRouteIdsKey = useMemo(() => {
    const ids = new Set<number>();
    if (projectionUiEnabled) {
      for (const p of Array.from(projectionIndex.values())) {
        for (const r of p.byRoute) {
          if (Number.isFinite(r.routeId) && r.routeId > 0) ids.add(r.routeId);
        }
      }
    } else {
      for (const v of Array.from(allocationIndex.values())) {
        for (const r of v.byRoute) {
          if (Number.isFinite(r.routeId) && r.routeId > 0) ids.add(r.routeId);
        }
      }
    }
    return Array.from(ids)
      .sort((a, b) => a - b)
      .join(',');
  }, [allocationIndex, projectionIndex, projectionUiEnabled]);

  const projectionIndexFingerprint = useMemo(() => {
    if (!projectionIndex.size) return '';
    return Array.from(projectionIndex.entries())
      .sort((a, b) => a[0] - b[0])
      .map(
        ([cid, p]) =>
          `${cid}:${p.inRoutesActive}:${p.inTransitActive}:${p.deliveredTotal}:${p.byRoute
            .map((x) => `${x.routeId}:${x.active}`)
            .sort()
            .join(',')}`
      )
      .join('|');
  }, [projectionIndex]);

  const routeStatusesFingerprint = useMemo(() => {
    if (!allocationRouteIdsKey) return '';
    return allocationRouteIdsKey
      .split(',')
      .map((idStr) => {
        const id = Number(idStr);
        return `${id}:${routeStatusById.get(id) ?? ''}`;
      })
      .join(',');
  }, [allocationRouteIdsKey, routeStatusById]);

  const cargoDetailsHistoryKey = useMemo(
    () =>
      `${pageApplicationIdsKey}|${projectionUiEnabled ? projectionIndexFingerprint : allocationIndexFingerprint}|${routeStatusesFingerprint}`,
    [
      pageApplicationIdsKey,
      allocationIndexFingerprint,
      projectionIndexFingerprint,
      projectionUiEnabled,
      routeStatusesFingerprint,
    ]
  );

  useEffect(() => {
    if (!allocationRouteIdsKey) {
      setRouteStatusById(new Map());
      setRouteDetailById(new Map());
      return;
    }
    const ids = allocationRouteIdsKey.split(',').map(Number).filter((n) => n > 0);
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            const r = await dispatch(
              routesApi.endpoints.getRouteById.initiate(id, { forceRefetch: true })
            ).unwrap();
            return { id, route: r, status: r?.status != null ? String(r.status) : '' };
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      const sm = new Map<number, string>();
      const rm = new Map<number, Route>();
      for (const row of results) {
        if (!row) continue;
        rm.set(row.id, row.route);
        if (row.status) sm.set(row.id, row.status);
      }
      setRouteDetailById(rm);
      setRouteStatusById(sm);
    })();
    return () => {
      cancelled = true;
    };
  }, [allocationRouteIdsKey, dispatch, fulfilledTimeStamp]);

  useEffect(() => {
    if (projectionUiEnabled) {
      setAllocationIndex(new Map());
      setAllocationIndexLoading(false);
      return;
    }
    if (!pageApplicationIdsKey) {
      setAllocationIndex(new Map());
      setAllocationIndexLoading(false);
      return;
    }
    const appIds = pageApplicationIdsKey.split(',').map(Number).filter((n) => n > 0);
    let cancelled = false;
    setAllocationIndexLoading(true);
    (async () => {
      try {
        const index = await fetchCargoAllocationIndexForApplications(dispatch, appIds);
        if (!cancelled) {
          setAllocationIndex(index);
        }
      } catch (e) {
        console.error('[CargoPage] allocation index failed', { pageApplicationIdsKey, e });
        message.warning('Не удалось загрузить распределение по маршрутам');
        if (!cancelled) {
          setAllocationIndex(new Map());
        }
      } finally {
        if (!cancelled) {
          setAllocationIndexLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageApplicationIdsKey, pageRoutesFingerprint, dispatch, projectionUiEnabled]);

  // admin detection
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '';
    if (!token) return;
    fetch(`${API_BASE_URL}/auth/profile`, {
      headers: { Authorization: token ? `Bearer ${token}` : '' }
    })
      .then((r) => r.json())
      .then((json) => {
        if (!mounted) return;
        const roleCandidate = json?.data?.role || json?.role || (json?.data?.user?.role);
        const roleName = typeof roleCandidate === 'string' ? roleCandidate : roleCandidate?.name;
        setIsAdmin(roleName === 'admin');
      })
      .catch(() => {
        if (!mounted) return;
        setIsAdmin(false);
      });
    return () => { mounted = false; };
  }, []);

  // selection + modal state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | undefined>(undefined);

  // Обработчики событий
  const handleFilterChange = (key: keyof CargoFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Сброс на первую страницу при изменении фильтров
    }));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit
    }));
  };

  const handleViewDetails = (cargo: ApplicationCargo) => {
    setSelectedCargo(cargo);
    setDetailsModalVisible(true);
  };

  const handleViewHistory = (cargo: ApplicationCargo) => {
    setSelectedCargo(cargo);
    setHistoryModalVisible(true);
  };

  const handleViewDocuments = (cargo: ApplicationCargo) => {
    setSelectedCargo(cargo);
    setDocumentsModalVisible(true);
  };

  const handleViewLocationHistory = (cargo: ApplicationCargo) => {
    setSelectedCargo(cargo);
    setLocationHistoryModalVisible(true);
  };

  // handleStatusChange currently not used in this page; keep implementation for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStatusChange = async (cargoId: number, newStatus: CargoStatus) => {
    try {
      await updateCargoStatus({ id: cargoId, status: newStatus }).unwrap();
      message.success('Статус груза обновлен');
    } catch (error) {
      message.error('Ошибка обновления статуса груза');
    }
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      applicationId: undefined
    });
  };

  const handleOpenMoveModal = () => {
    setMoveModalOpen(true);
  };
  const handleConfirmMove = async () => {
    try {
      if (!selectedWarehouseId || selectedRowKeys.length === 0) {
        message.warning('Выберите склад и хотя бы один груз');
        return;
      }
      await moveBulk({
        warehouseId: selectedWarehouseId,
        cargoIds: (selectedRowKeys as number[])
      }).unwrap();
      message.success('Грузы приняты на склад');
      setMoveModalOpen(false);
      setSelectedWarehouseId(undefined);
      setSelectedRowKeys([]);
      refetch();
    } catch (e: any) {
      message.error(e?.data?.error || 'Ошибка перемещения на склад');
    }
  };

  const columns = useMemo(
    () => [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (text: string, record: ApplicationCargo) => (
        <div>
          <Text strong>{text}</Text>
          {record.description && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.description}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Заявка',
      key: 'application',
      width: 150,
      render: (record: ApplicationCargo) => {
        const app = record.application;
        return (
          <div>
            <Text strong>№{app?.internalNumber}</Text>
            {app && (app.externalNumber || app.applicationDate) && (
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  ({app.externalNumber || '—'}{app.applicationDate ? ` от ${dayjs(app.applicationDate).format('DD.MM.YYYY')}` : ''})
                </Text>
              </div>
            )}
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {app?.client?.name}
              </Text>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Количество',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'center' as const,
      render: (quantity: number) => <Badge count={quantity} color="blue" />
    },
    {
      title: 'Габариты (м)',
      key: 'dimensions',
      width: 120,
      align: 'center' as const,
      render: (record: ApplicationCargo) => {
        return (
          <Text type="secondary">
            {formatDimSafe(record.lengthM)}×{formatDimSafe(record.widthM)}×{formatDimSafe(record.heightM)}
          </Text>
        );
      }
    },
    {
      title: 'Общий вес (кг)',
      dataIndex: 'totalWeightKg',
      key: 'totalWeightKg',
      width: 120,
      align: 'center' as const,
      render: (weight: number) => (
        <Text strong>{weight ? Number(weight).toFixed(1) : '—'}</Text>
      )
    },
    {
      title: 'Объем (м³)',
      dataIndex: 'volumeM3',
      key: 'volumeM3',
      width: 100,
      align: 'center' as const,
      render: (volume: number) => (
        <Text>{volume ? Number(volume).toFixed(3) : '—'}</Text>
      )
    },
    {
      title: 'В маршрутах (шт)',
      key: 'inRoutesQty',
      width: 120,
      align: 'center' as const,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        return <Text strong>{snap.inRoutes.toLocaleString('ru-RU')}</Text>;
      }
    },
    {
      title: 'В пути (шт)',
      key: 'inTransitQty',
      width: 110,
      align: 'center' as const,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        return <Text strong>{snap.inTransit.toLocaleString('ru-RU')}</Text>;
      }
    },
    {
      title: 'Свободно (шт)',
      key: 'freeQty',
      width: 120,
      align: 'center' as const,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        return <Text strong>{snap.available.toLocaleString('ru-RU')}</Text>;
      }
    },
    {
      title: 'Доставлено (шт)',
      key: 'deliveredQty',
      width: 120,
      align: 'center' as const,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        return <Text strong>{snap.delivered.toLocaleString('ru-RU')}</Text>;
      }
    },
    {
      title: 'Перевозка',
      key: 'transportStatus',
      width: 200,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        return <Tag color={transportStatusTagColor(snap.transportLabel)}>{snap.transportLabel}</Tag>;
      }
    },
    {
      title: 'Направление',
      key: 'routeDirections',
      width: 240,
      ellipsis: true,
      render: (_: unknown, record: ApplicationCargo) => {
        if (countersLoading) return <Spin size="small" />;
        const snap = resolveCounters(record);
        if (snap.byRoute.length === 0) {
          const cr = record.currentRoute;
          if (cr?.id) {
            const r = routeDetailById.get(cr.id) ?? cr;
            return <Text type="secondary">{buildRouteDirection(r)}</Text>;
          }
          return <Text type="secondary">—</Text>;
        }
        return (
          <Space direction="vertical" size={2}>
            {snap.byRoute.map((row) => {
              const r = routeDetailById.get(row.routeId);
              const label = r ? buildRouteDirection(r) : '…';
              const qtyLabel = projectionUiEnabled ? row.active : row.amount;
              return (
                <Text key={`${record.id}-${row.routeId}`} type="secondary" style={{ fontSize: 12 }}>
                  №{row.routeId}: {label} · {qtyLabel} мест
                </Text>
              );
            })}
          </Space>
        );
      }
    },
    {
      title: 'Статус (БД)',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (status: CargoStatus) => (
        <Tag color={CARGO_STATUS_COLORS[status]}>{CARGO_STATUS_LABELS[status]}</Tag>
      ),
      filters: Object.values(CargoStatus).map((status) => ({
        text: CARGO_STATUS_LABELS[status],
        value: status
      })),
      onFilter: (value: any, record: ApplicationCargo) => record.status === value
    },
    {
      title: 'Текущее местоположение',
      key: 'location',
      width: 180,
      render: (record: ApplicationCargo) => {
        if (record.currentRoute) {
          const cr = record.currentRoute;
          const r = routeDetailById.get(cr.id) ?? cr;
          const direction = buildRouteDirection(r);
          return (
            <div>
              <Text type="secondary">В маршруте:</Text>
              <div>
                <Tag color="orange">{direction || '—'}</Tag>
              </div>
            </div>
          );
        }
        if (record.currentWarehouse) {
          return (
            <div>
              <Text type="secondary">На складе:</Text>
              <div>
                <Text strong>{record.currentWarehouse.name}</Text>
              </div>
            </div>
          );
        }
        return <Text type="secondary">В заявке</Text>;
      }
    },
    {
      title: 'Дата создания',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: ApplicationCargo) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Просмотреть детали">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Фотографии">
            <Button
              type="text"
              icon={<PictureOutlined />}
              onClick={() => handleViewDocuments(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="История перемещений">
            <Button
              type="text"
              icon={<HistoryOutlined />}
              onClick={() => handleViewHistory(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="История местоположений">
            <Button
              type="text"
              icon={<EnvironmentOutlined />}
              onClick={() => handleViewLocationHistory(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      )
    }
    ],
    [countersLoading, routeDetailById, projectionUiEnabled, resolveCounters]
  );

  // Подсчет статистики
  const statistics = useMemo(() => {
    if (!cargoResponse?.data?.cargo) return null;
    
    const stats = cargoResponse.data.cargo.reduce((acc: Record<CargoStatus, number>, cargo: ApplicationCargo) => {
      acc[cargo.status] = (acc[cargo.status] || 0) + 1;
      return acc;
    }, {} as Record<CargoStatus, number>);
    
    return stats;
  }, [cargoResponse?.data?.cargo]);

  return (
    <PageLayout
      title="Управление грузами"
      subtitle="Отслеживание всех грузов в системе"
      actions={
        <Space>
          {isAdmin && (
            <Button
              type="primary"
              onClick={handleOpenMoveModal}
              disabled={selectedRowKeys.length === 0}
              loading={isMoving}
            >
              Принять на склад
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading}>
            Обновить
          </Button>
        </Space>
      }
    >
      {/* Статистика */}
      {statistics && (
        <Row gutter={16} className="mb-4">
          {Object.entries(CARGO_STATUS_LABELS).map(([status, label]) => (
            <Col span={6} key={status}>
              <Card size="small" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: CARGO_STATUS_COLORS[status as CargoStatus] }}>
                    {statistics[status as CargoStatus] || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>{label}</div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Фильтры */}
      <Card className="mb-4" style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Input
              placeholder="Поиск по наименованию..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Статус"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
              style={{ width: '100%' }}
            >
              {Object.entries(CARGO_STATUS_LABELS).map(([status, label]) => (
                <Option key={status} value={status}>{label}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Заявка"
              value={filters.applicationId}
              onChange={(value) => handleFilterChange('applicationId', value)}
              allowClear
              showSearch
              optionFilterProp="children"
              style={{ width: '100%' }}
            >
              {applications.map((a: any) => (
                <Option key={a.id} value={a.id}>
                  №{a.internalNumber}
                  {a.externalNumber || a.applicationDate ? ` (${a.externalNumber || '—'}${a.applicationDate ? ` от ${dayjs(a.applicationDate).format('DD.MM.YYYY')}` : ''})` : ''}
                  {a.client?.name ? ` — ${a.client.name}` : ''}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              placeholder={['Дата от', 'Дата до']}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('dateFrom', dates[0]?.format('YYYY-MM-DD'));
                  handleFilterChange('dateTo', dates[1]?.format('YYYY-MM-DD'));
                } else {
                  handleFilterChange('dateFrom', undefined);
                  handleFilterChange('dateTo', undefined);
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Button 
              icon={<FilterOutlined />} 
              onClick={handleClearFilters}
            >
              Сбросить фильтры
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Таблица грузов */}
      <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <DataTable
          columns={columns}
          dataSource={cargoResponse?.data?.cargo || []}
          loading={isLoading}
          rowKey="id"
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          onRow={(record) => ({
            onClick: () => handleViewDetails(record),
            style: { cursor: 'pointer' as const },
          })}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="middle"
        />
        
        {/* Пагинация */}
        {cargoResponse?.data?.pagination && (
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              current={cargoResponse.data.pagination.page}
              pageSize={cargoResponse.data.pagination.limit}
              total={cargoResponse.data.pagination.total}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              showSizeChanger
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} из ${total} грузов`
              }
            />
          </div>
        )}
      </Card>

      {/* Модальное окно деталей груза */}
      <CargoDetailsModal
        cargo={selectedCargo}
        visible={detailsModalVisible}
        onClose={() => setDetailsModalVisible(false)}
        counterSnapshot={
          selectedCargo?.id != null
            ? resolveCargoCounterSnapshot({
                cargoId: selectedCargo.id,
                quantity: Number(selectedCargo.quantity ?? 0),
                projectionIndex,
                legacyIndex: allocationIndex,
                routeStatusById,
                useProjection: projectionUiEnabled,
              })
            : null
        }
        counterSnapshotLoading={countersLoading && detailsModalVisible}
        projectionFailed={projectionUiEnabled && projectionIndexFailed}
        historyRefreshKey={cargoDetailsHistoryKey}
      />

      {/* Модальное окно истории груза */}
      <CargoHistoryModal
        visible={historyModalVisible}
        onClose={() => setHistoryModalVisible(false)}
        cargoId={selectedCargo?.id || 0}
        cargoName={selectedCargo?.name}
      />

      {/* Модальное окно документов груза */}
      <CargoDocumentsModal
        visible={documentsModalVisible}
        cargo={selectedCargo}
        onClose={() => setDocumentsModalVisible(false)}
      />

      {/* Модальное окно истории местоположений груза */}
      <LocationHistoryModal
        visible={locationHistoryModalVisible}
        cargoId={selectedCargo?.id}
        onClose={() => setLocationHistoryModalVisible(false)}
        title={`История местоположений - ${selectedCargo?.name}`}
      />

      {/* Модалка перемещения на склад (только админ) */}
      {isAdmin && (
        <Card
          style={{ display: moveModalOpen ? 'block' : 'none', position: 'fixed', right: 24, bottom: 24, zIndex: 1000, width: 360 }}
          title="Принять на склад"
          extra={<Button onClick={() => setMoveModalOpen(false)}>Закрыть</Button>}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Выбрано грузов: {selectedRowKeys.length}</Text>
            <Select
              placeholder="Выберите склад"
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="children"
              value={selectedWarehouseId}
              onChange={(v) => setSelectedWarehouseId(v)}
            >
              {warehouses?.map(w => (
                <Option key={w.id} value={w.id}>
                  {w.name} — {w.city}
                </Option>
              ))}
            </Select>
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={() => setMoveModalOpen(false)}>Отмена</Button>
              <Button type="primary" onClick={handleConfirmMove} loading={isMoving} disabled={!selectedWarehouseId || selectedRowKeys.length === 0}>
                Принять
              </Button>
            </Space>
          </Space>
        </Card>
      )}
    </PageLayout>
  );
};

export default CargoPage; 